import { commonHeaders } from "../ClientResources";
import { deviceLinkLocalhostRoot } from "./Applications";
import { faroFetch } from "./Devices";

export enum EcgFileOpenResult {
    FailedToLoad = 0,
    RhythmOnly = 1,
    RepresentativeBeat = 2,
    GraphicsLoaded = 3
}

export interface LeadAnnotation {
    annotation: string;
    leadIndex: number;
    beatIndex: number;
    value: number;
}

export enum CalEcgPermissions {
    ModifyDemographics = "MoDemog",
    ViewDemographics = "ViewDemog",
    MoveAnnotations = "MoAnn",
    MarkLocalAnnotationsRhythm = "MkNwLocECGAnn",
    MarkGlobalAnnotationsRhythm = "MkNwGblECGAnn",
    MarkLocalAnnotationsRepBeat = "MkNwLocRPAnn",
    MarkGlobalAnnotationsRepBeat = "MkNwGblRPAnn",
    DeleteAnnotations = "DeAnn",
    MarkAnnotationsWithLowPixelToSampleRatio = "MkAnnRL",
    RunSemiAutomaticMode = "RuSeAut",
    CreateMedianBeats = "MkMedian",
    ModifyHeartRate = "MoHR",
    ComputeHeartRate = "AutoHR",
    AutomaticInterpretation = "RunAutInter",
    AutomaticAxes = "RunAutAxes",
    SelectRRMeasurements = "ModRRPlacing",
    SelectTPeakandTOff = "ModTpeakToffPlacing",
    All = "All"
}

export const OpenEcg = (appId: string, hl7Base64String: string): Promise<EcgFileOpenResult> => {
    return faroFetch(`${deviceLinkLocalhostRoot}/applications/${appId}/openecg`, {
        method: 'POST',
        ...commonHeaders,
        body: JSON.stringify(hl7Base64String),
    })
        .then(response => response.json());
}

export const OpenComparisonEcg = (appId: string, hl7Base64String: string): Promise<EcgFileOpenResult> => {
    return faroFetch(`${deviceLinkLocalhostRoot}/applications/${appId}/opencomparisonecg`, {
        method: 'POST',
        ...commonHeaders,
        body: JSON.stringify(hl7Base64String),
    })
        .then(response => response.json());
}

export const OpenSecondComparisonEcg = (appId: string, hl7Base64String: string): Promise<EcgFileOpenResult> => {
    return faroFetch(`${deviceLinkLocalhostRoot}/applications/${appId}/opensecondcomparisonecg`, {
        method: 'POST',
        ...commonHeaders,
        body: JSON.stringify(hl7Base64String),
    })
        .then(response => response.json());
}

export const Protocol = (appId: string): Promise<boolean> => {
    return faroFetch(`${deviceLinkLocalhostRoot}/applications/${appId}/protocol`, {
        method: 'POST',
        ...commonHeaders,
    })
        .then(response => handleResponse(response, "Protocol"));
}

export const ComparisonProtocol = (appId: string): Promise<boolean> => {
    return faroFetch(`${deviceLinkLocalhostRoot}/applications/${appId}/comparisonprotocol`, {
        method: 'POST',
        ...commonHeaders,
    })
        .then(response => handleResponse(response, "ComparisonProtocol"));
}

export const SecondComparisonProtocol = (appId: string): Promise<boolean> => {
    return faroFetch(`${deviceLinkLocalhostRoot}/applications/${appId}/secondcomparisonprotocol`, {
        method: 'POST',
        ...commonHeaders,
    })
        .then(response => handleResponse(response, "SecondComparisonProtocol"));
}

export const ShowWindow = (appId: string, isMaximized: boolean): Promise<boolean> => {
    return faroFetch(`${deviceLinkLocalhostRoot}/applications/${appId}/showwindow`, {
        method: 'POST',
        ...commonHeaders,
        body: JSON.stringify(isMaximized),
    })
        .then(response => handleResponse(response, "ShowWindow"));
}

export const ShowComparisonWindow = (appId: string, isMaximized: boolean): Promise<boolean> => {
    return faroFetch(`${deviceLinkLocalhostRoot}/applications/${appId}/showcomparisonwindow`, {
        method: 'POST',
        ...commonHeaders,
        body: JSON.stringify(isMaximized),
    })
        .then(response => handleResponse(response, "ShowComparisonWindow"));
}

export const ShowSecondComparisonWindow = (appId: string, isMaximized: boolean): Promise<boolean> => {
    return faroFetch(`${deviceLinkLocalhostRoot}/applications/${appId}/showsecondcomparisonwindow`, {
        method: 'POST',
        ...commonHeaders,
        body: JSON.stringify(isMaximized),
    })
        .then(response => handleResponse(response, "ShowSecondComparisonWindow"));
}

export const ShowAllWindows = (appId: string): Promise<boolean> => {
    return faroFetch(`${deviceLinkLocalhostRoot}/applications/${appId}/showallwindows`, {
        method: 'POST',
        ...commonHeaders
    })
        .then(response => handleResponse(response, "ShowAllWindows"));
}

export const CloseApplication = (appId: string): Promise<boolean> => {
    return faroFetch(`${deviceLinkLocalhostRoot}/applications/${appId}/closeapplication`, {
        method: 'POST',
        ...commonHeaders,
    })
        .then(response => handleResponse(response, "CloseApplication"));
}

export const GetAnnotations = (appId: string): Promise<any> => {
    return faroFetch(`${deviceLinkLocalhostRoot}/applications/${appId}/getannotations`, {
        method: 'POST',
        ...commonHeaders,
    })
        .then(response => response.json());
}

export const ExportEcgToBase64 = (appId: string, format: number): Promise<string> => {
    return faroFetch(`${deviceLinkLocalhostRoot}/applications/${appId}/exportecgtobase64`, {
        method: 'POST',
        ...commonHeaders,
        body: JSON.stringify({
            fullFileName: "",
            format: format
        }),
    })
        .then(response => response.json());
}

export const ImportAnnotationsTextFile = (appId: string, base64FileContent: string): Promise<number> => {
    return faroFetch(`${deviceLinkLocalhostRoot}/applications/${appId}/importannotationstextfile`, {
        method: 'POST',
        ...commonHeaders,
        body: JSON.stringify(base64FileContent),
    })
        .then(response => response.json());
}

export const ImportComparisonAnnotationsTextFile = (appId: string, base64FileContent: string): Promise<number> => {
    return faroFetch(`${deviceLinkLocalhostRoot}/applications/${appId}/importcomparisonannotationstextfile`, {
        method: 'POST',
        ...commonHeaders,
        body: JSON.stringify(base64FileContent),
    })
        .then(response => response.json());
}

export const ImportSecondComparisonAnnotationsTextFile = (appId: string, base64FileContent: string): Promise<number> => {
    return faroFetch(`${deviceLinkLocalhostRoot}/applications/${appId}/importsecondcomparisonannotationstextfile`, {
        method: 'POST',
        ...commonHeaders,
        body: JSON.stringify(base64FileContent),
    })
        .then(response => response.json());
}

export const SetTitle = (appId: string, title: string): Promise<boolean> => {
    return faroFetch(`${deviceLinkLocalhostRoot}/applications/${appId}/settitle`, {
        method: 'POST',
        ...commonHeaders,
        body: JSON.stringify(title),
    })
        .then(response => handleResponse(response, "SetTitle"));
}

export const SetComparisonTitle = (appId: string, title: string): Promise<boolean> => {
    return faroFetch(`${deviceLinkLocalhostRoot}/applications/${appId}/setcomparisontitle`, {
        method: 'POST',
        ...commonHeaders,
        body: JSON.stringify(title),
    })
        .then(response => handleResponse(response, "SetComparisonTitle"));
}

export const SetSecondComparisonTitle = (appId: string, title: string): Promise<boolean> => {
    return faroFetch(`${deviceLinkLocalhostRoot}/applications/${appId}/setsecondcomparisontitle`, {
        method: 'POST',
        ...commonHeaders,
        body: JSON.stringify(title),
    })
        .then(response => handleResponse(response, "SetSecondComparisonTitle"));
}

export const SetPermission = (appId: string, permission: string, enabled: boolean): Promise<boolean> => {
    return faroFetch(`${deviceLinkLocalhostRoot}/applications/${appId}/setpermission`, {
        method: 'POST',
        ...commonHeaders,
        body: JSON.stringify({
            permission: permission,
            enabled: enabled,
        }),
    })
        .then(response => handleResponse(response, "SetPermission"));
}

export const SetComparisonPermission = (appId: string, permission: string, enabled: boolean): Promise<boolean> => {
    return faroFetch(`${deviceLinkLocalhostRoot}/applications/${appId}/setcomparisonpermission`, {
        method: 'POST',
        ...commonHeaders,
        body: JSON.stringify({
            permission: permission,
            enabled: enabled,
        }),
    })
        .then(response => handleResponse(response, "SetComparisonPermission"));
}

export const SetSecondComparisonPermission = (appId: string, permission: string, enabled: boolean): Promise<boolean> => {
    return faroFetch(`${deviceLinkLocalhostRoot}/applications/${appId}/setsecondcomparisonpermission`, {
        method: 'POST',
        ...commonHeaders,
        body: JSON.stringify({
            permission: permission,
            enabled: enabled,
        }),
    })
        .then(response => handleResponse(response, "SetSecondComparisonPermission"));
}

export const SetDigitalDefaults = (appId: string): Promise<boolean> => {
    return faroFetch(`${deviceLinkLocalhostRoot}/applications/${appId}/setdigitaldefaults`, {
        method: 'POST',
        ...commonHeaders,
        body: JSON.stringify({}),
    })
        .then(response => handleResponse(response, "SetDigitalDefaults"));
}

export const CreateInstance = (appId: string): Promise<boolean> => {
    return faroFetch(`${deviceLinkLocalhostRoot}/applications/${appId}/createinstance`, {
        method: 'POST',
        ...commonHeaders,
        body: JSON.stringify({}),
    })
        .then(response => handleResponse(response, "CreateInstance"));
}

export const CreateComparisonInstance = (appId: string): Promise<boolean> => {
    return faroFetch(`${deviceLinkLocalhostRoot}/applications/${appId}/createcomparisoninstance`, {
        method: 'POST',
        ...commonHeaders,
        body: JSON.stringify({}),
    })
        .then(response => handleResponse(response, "CreateComparisonInstance"));
}

export const CreateSecondComparisonInstance = (appId: string): Promise<boolean> => {
    return faroFetch(`${deviceLinkLocalhostRoot}/applications/${appId}/createsecondcomparisoninstance`, {
        method: 'POST',
        ...commonHeaders,
        body: JSON.stringify({}),
    })
        .then(response => handleResponse(response, "CreateSecondComparisonInstance"));
}

const handleResponse = (response: Response, functionName: string) => {
    if (response.ok)
        return response.ok;
    throw Error(`Failed to call '${functionName}'`);
}
